import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DatePicker as MuiXDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import { InputAdornment, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/pro-light-svg-icons';
import { defaultDateFormat, formatDate, toDate, correctCentury, toStartOfDay } from '../../utilities';
import { TextField } from '../text-field';
import { DatePickerValue, IDatePickerProps } from './date-picker';
import { validateRelativeDateFormat, validateDefaultDateFormat, validateExpandedDateFormat } from './date-validators';
import { useFeatureToggles } from '../../feature-toggles';
import { PICKER_TYPING_WITH_OPEN_POPPER_TOGGLE } from './picker-typing-toggle';
import { useReleaseFocusWhenPickerCloses } from './use-release-focus-when-picker-closes';

function validate(s: string, validators: Array<(value: string) => DatePickerValue>) {
  for (const validator of validators) {
    const result = validator(s);
    if (result) {
      return result;
    }
  }
  return undefined;
}

export function MuiDatePicker(props: IDatePickerProps) {
  const {
    value,
    onChange,
    name,
    className,
    label,
    minDate,
    maxDate,
    openToDate,
    required,
    readOnly,
    error,
    errorText,
    disabled,
    inlineDisplay,
    warningText,
    validators,
    invalidDateMessage = 'Indtast en gyldig dato',
    variant = 'filled',
    fullWidth,
    helperText,
    onCalendarOpen,
    onCalendarClose,
    onInternalValidation,
    disableAutoOpen = false,
    enableRelativeDateFormatting = false,
    shouldDisableDate,
  } = props;

  const defaultValidators = enableRelativeDateFormatting
    ? [validateRelativeDateFormat, validateDefaultDateFormat, validateExpandedDateFormat]
    : [validateDefaultDateFormat, validateExpandedDateFormat];
  const finalValidators = validators || defaultValidators;

  const [selected, setSelected] = useState(toDate(value));
  const [rawState, setRawState] = useState(formatDate(value));
  const [isOpen, setIsOpen] = useState(false);
  const [rangeError, setRangeError] = useState<string | undefined>();
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpenedFromFieldRef = useRef(false);
  const popperRef = useRef<HTMLDivElement>(null);

  const { isFeatureEnabled } = useFeatureToggles();
  const keepFocusInField = isFeatureEnabled(PICKER_TYPING_WITH_OPEN_POPPER_TOGGLE);
  const { handleFieldFocus, keepFocusAfterPick } = useReleaseFocusWhenPickerCloses(isOpen, keepFocusInField, popperRef);

  // The calendar always takes focus when it opens, so give it back to the field the user clicked to type in.
  useEffect(() => {
    if (!isOpen || !keepFocusInField || !isOpenedFromFieldRef.current) {
      return undefined;
    }
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isOpen, keepFocusInField]);

  useEffect(() => {
    setRawState(formatDate(value));
    setSelected(toDate(value));
  }, [value]);

  const apply = useCallback(() => {
    if (!rawState) {
      onChange(undefined);
      setRangeError(undefined);
      onInternalValidation?.(undefined);
      return;
    }

    let validatedDate = validate(rawState, finalValidators);
    let validationError: string | undefined;

    if (validatedDate) {
      validatedDate = correctCentury(validatedDate);

      // Validate against min/max date constraints
      if (validatedDate instanceof Date) {
        const dateTime = validatedDate.getTime();
        if (minDate && dateTime < minDate.getTime()) {
          validationError = `Dato må ikke være før ${formatDate(minDate)}`;
        } else if (maxDate && dateTime > maxDate.getTime()) {
          validationError = `Dato må ikke være efter ${formatDate(maxDate)}`;
        }
      }

      setRangeError(validationError);
      onChange(validatedDate);
    } else {
      validationError = invalidDateMessage;
      setRangeError(undefined);
    }

    onInternalValidation?.(validationError);
  }, [rawState, finalValidators, onChange, onInternalValidation, invalidDateMessage, minDate, maxDate]);

  function handleCalendarChange(date: Date | null) {
    keepFocusAfterPick();
    const val = date == null ? undefined : date;
    setSelected(toDate(val));
    setRawState(formatDate(val));
    setRangeError(undefined);
    onChange(val);
    onInternalValidation?.(undefined);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setRawState(event.target.value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      apply();
      if (keepFocusInField) {
        setIsOpen(false);
      }
    }
  }

  function handleFieldClick(event: React.MouseEvent) {
    if (disabled || readOnly) {
      return;
    }
    if (keepFocusInField && isOpen) {
      // Keeps the calendar's click-away listener on document from closing it while the user types.
      event.stopPropagation();
      return;
    }
    isOpenedFromFieldRef.current = true;
    setIsOpen(true);
  }

  function handleIconClick(event: React.MouseEvent) {
    event.stopPropagation();
    if (!disabled && !readOnly) {
      isOpenedFromFieldRef.current = false;
      setIsOpen(prev => !prev);
    }
  }

  function handleBlur() {
    apply();
  }

  function handleOpen() {
    setIsOpen(true);
    onCalendarOpen?.();
  }

  function handleClose() {
    setIsOpen(false);
    onCalendarClose?.();
  }

  const dateError = rawState && validate(rawState, finalValidators) === undefined ? invalidDateMessage : undefined;
  const displayError = rangeError ?? dateError;

  let wrapperClassName = '';
  if (inlineDisplay) {
    wrapperClassName = 'inline-display';
  }

  const textFieldClasses = ['date-picker__text-field', className, fullWidth && 'full-width'].filter(Boolean).join(' ');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da} localeText={{ todayButtonLabel: 'I dag' }}>
      <div className={wrapperClassName || undefined} ref={triggerRef}>
        <TextField
          inputRef={inputRef}
          className={textFieldClasses}
          label={label}
          value={rawState}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={disableAutoOpen ? undefined : handleFieldClick}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="large" onClick={handleIconClick}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="xs" />
                </IconButton>
              </InputAdornment>
            ),
            readOnly,
          }}
          error={error || !!displayError}
          errorText={(error || !!displayError) && (errorText || (displayError ? String(displayError) : undefined))}
          helperText={warningText || helperText}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          fullWidth={fullWidth}
          variant={variant}
          multiline={false}
          inputProps={{
            autoComplete: 'off',
            placeholder: defaultDateFormat,
            name,
            onFocus: handleFieldFocus,
          }}
        />
        {/* Callers often pass maxDate={new Date()}, so a millisecond key would remount the calendar on every render and drop focus. */}
        <MuiXDatePicker
          key={keepFocusInField ? `${formatDate(minDate)}-${formatDate(maxDate)}` : `${minDate?.getTime()}-${maxDate?.getTime()}`}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          value={selected ?? null}
          onChange={handleCalendarChange}
          minDate={minDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          referenceDate={selected ? undefined : toStartOfDay(openToDate) ?? undefined}
          disabled={disabled}
          readOnly={readOnly}
          displayWeekNumber
          showDaysOutsideCurrentMonth
          slots={{ textField: () => null }}
          slotProps={{
            desktopTrapFocus: { disableEnforceFocus: keepFocusInField },
            popper: {
              ref: popperRef,
              anchorEl: triggerRef.current,
              placement: 'bottom-start',
            },
            actionBar: {
              actions: ['today'],
            },
            toolbar: { hidden: true },
          }}
        />
      </div>
    </LocalizationProvider>
  );
}
